import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { View, Pressable } from "react-native-web";
import TextField from '@mui/material/TextField';
import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../utils/styling';
import { useState } from 'react';
import CustomText from '../../components/atoms/CustomText';
import ProcessingResults from '../../components/common/ProcessingResults';
import { useSearchAccountByEmail } from '../../lib/salesforce';
import { useCreateLead, useUpdateQuizWithAccount } from '../../lib/quizzes';
import { getResultsDataFromStateOrStorage, ResultsData } from "../../utils/resultsData";
import { ADVISOR_ATTRIBUTION_KEY } from "../../utils/quizRetake";
import CircularProgress from "@mui/material/CircularProgress";

// ResultsData type imported from utils/resultsData

export default function ContactSubmission() {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");

  // Hooks for API calls
  const { mutateAsync: searchAccountByEmail } = useSearchAccountByEmail();
  const { mutateAsync: createLead } = useCreateLead();
  const { mutateAsync: updateQuizWithSf } = useUpdateQuizWithAccount();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email) {
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email.")
      return;
    }

    setIsSubmitting(true);

    try {
      const resultsData = getResultsDataFromStateOrStorage(location.state) as ResultsData;
      console.log("submitting with resultsData passed in", resultsData);
      const quizId = resultsData.quizId!;
      console.log('🔍 Searching for Salesforce account by email:', formData.email);

      const emailSearchResult = await searchAccountByEmail(formData.email);
      // Keep the advisor's client id from their quiz link when the typed email doesn't
      // match a Salesforce account, so the advisor still gets the result.
      const sfAccount = emailSearchResult.found
        ? emailSearchResult.account.Id
        : (resultsData.sfAccount ?? null);
      
      console.log('📧 Email search result:', { found: emailSearchResult.found, sfAccount });

      // Update quiz record with sfAccount if we found one
      if (sfAccount) {
        console.log("Found sfAccount, updating quiz record");
        await updateQuizWithSf({quizId, sfAccount});
        console.log('✅ Quiz updated:', quizId);
      }

      // Create lead record
      console.log('👤 Creating lead');
      const leadData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        sf_account: sfAccount,
        quiz: quizId,
        // Set when the quiz was opened from an advisor's link or QR (86bagxuwz).
        advisor_public_id: localStorage.getItem(ADVISOR_ATTRIBUTION_KEY) || undefined,
      };
      
      const lead = await createLead(leadData);
      console.log('✅ Lead created:', lead);

      // Enhanced results data with quiz info
      const enhancedResultsData = {
        ...resultsData,
        sfAccount: sfAccount,
        leadId: lead.id,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      };

      console.log('enhanced results', enhancedResultsData);

      // Show processing overlay instead of navigating immediately
      setShowProcessingOverlay(true);

      // store enhanced results in localStorage
      try {
        localStorage.setItem("mpay_resultsData", JSON.stringify(enhancedResultsData));
        console.log('✅ Enhanced results stored to localStorage');
      } catch (error) {
        console.error('❌ Failed to store results to localStorage:', error);
      }

      setTimeout(() => {
        // Navigate to main results
        navigate('/quiz/results-ready', { 
          state: { 
            ...enhancedResultsData, 
            from: location.pathname 
          } 
        });
        setShowProcessingOverlay(false);
      }, 6500);

    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProcessingComplete = () => {
    setShowProcessingOverlay(false);
    const resultsData = location.state as ResultsData;
    
    // Navigate to main results with enhanced data
    navigate('/quiz/results-ready', { 
      state: { 
        ...resultsData,
        from: location.pathname 
      } 
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (formRef && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  return !showProcessingOverlay ? (
      <View style={{ flex: 1, position: 'relative', minHeight: '100vh' }}>
        <View 
          style={{ 
            width: '100%', 
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: isMobile ? spacing.md : spacing.xl,
            paddingLeft: isMobile ? spacing.lg : spacing.xxl, 
            paddingRight: isMobile ? spacing.lg : spacing.xxl, 
            marginBottom: spacing.xxl,
          }}
          ref={formRef}
        >
          <View
            style={{
              backgroundColor: colors.white,
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              padding: isMobile ? spacing.md : spacing.xl,
              maxWidth: 900,
              width: "100%",
              marginBottom: spacing.xxl,
              border: '1px solid black',
            }}
          >
            <CustomText style={{
              fontSize: isMobile ? 24 : 32,
              fontWeight: '700',
              color: colors.dark,
              textAlign: 'center',
              marginBottom: isMobile ? spacing.md : spacing.xl,
              fontFamily: "Nunito",
            }}>
              Get Your Results
            </CustomText>
            
            <form onSubmit={handleSubmit} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.lg,
            }}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                variant="outlined"
                disabled={isSubmitting}
              />

              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
                disabled={isSubmitting}
              />
              
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                disabled={isSubmitting}
              />

              <TextField
                fullWidth
                label="Phone Number (optional)"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                disabled={isSubmitting}
              />
              
              <Pressable
                style={{
                  marginTop: isMobile ? spacing.sm : spacing.md,
                  width: '100%',
                  height: 48,
                  opacity: isSubmitting ? 0.7 : 1,
                  backgroundColor: 'black',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
                disabled={isSubmitting}
                onPress={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
              >
                <CustomText style={{
                  color: 'white',
                  fontFamily: 'Nunito', // Change this to your desired font
                  fontSize: isMobile ? 18 : 24,
                  fontWeight: '700',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "View My Results"}
                </CustomText>
              </Pressable>
            </form>
            {error && (
              <CustomText style={{
                fontFamily: "Nunito",
                textAlign: 'center',
                color: 'red',
                marginTop: isMobile ? spacing.sm : spacing.md,
                fontWeight: 700,
              }}>
                {error}
              </CustomText>
            )}
          </View>
        </View>
      </View>
    ) : (
        <View style={{
          marginTop: isMobile ? spacing.lg : spacing.xl,
          position: 'relative',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {(() => {
            // Unified shape: results are passed directly in location.state
            const stateAny: any = location.state || {};
            const answers = stateAny?.answers || [];
            const clientId = stateAny?.sfAccount;
            return (
              <ProcessingResults
                answers={answers}
                onReset={handleProcessingComplete}
                clientId={clientId}
              />
            );
          })()}
        </View>
      )
}
